<script setup lang="ts">
import Modal from 'primevue/dialog';

interface Props {
  visible?: boolean;
  title?: string;
  backEnabled?: boolean;
}

interface Emits {
  (e: 'hide'): void;
  (e: 'back'): void;
  (e: 'update:visible', value: boolean): void;
}

withDefaults(defineProps<Props>(), {
  visible: false,
  title: '',
  backEnabled: false,
});

const emit = defineEmits<Emits>();

const handleVisibilityChange = (value: boolean): void => {
  emit('update:visible', value);
  if (!value) {
    emit('hide');
  }
};
</script>

<template>
  <Modal
    :visible="visible"
    :modal="true"
    :draggable="false"
    append-to="self"
    :class="$style['split-modal']"
    @update:visible="handleVisibilityChange"
  >
    <template #header>
      <i
        v-if="backEnabled"
        :class="$style['back-icon']"
        class="pi pi-chevron-left"
        @click="$emit('back')"
      ></i>

      <h2 v-if="title || $slots.title" :class="$style['modal-title']">
        <slot name="title">{{ title }}</slot>
      </h2>
    </template>

    <div :class="$style.content">
      <slot name="content" />
    </div>

    <template #footer>
      <slot name="footer" />
    </template>

    <div :class="$style['image-container']">
      <slot name="image" />
    </div>
  </Modal>
</template>

<style lang="less" module>
html body .split-modal [class^='p-dialog-content'] {
  margin: 0;
  padding: 0;
}

html body .split-modal [class^='p-dialog-header'] {
  padding: 24px 40px;
}

html body .split-modal.split-modal [class^='p-dialog-header-icon'] {
  top: unset;
  padding: unset;
}

html body .split-modal [class^='p-dialog-footer'] {
  padding: 16px 40px 24px;
}

.back-icon {
  cursor: pointer;
  font-size: 20px;
  color: @black;
  position: absolute;
  left: 40px;
  top: unset;
  z-index: 10;
  border-radius: 50%;
  padding: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgb(232, 232, 234);
  }
}

.modal-title {
  font-family: @regular-font;
  font-size: 24px;
  font-weight: 600;
  color: @black;
  margin: 0;
}

.image-container {
  background: linear-gradient(135deg, #e8e4ff 0%, #d1c8ff 100%);
}

.content {
  padding: 24px 40px;
}

@media (min-width: 1280px) {
  .split-modal {
    padding-right: 362px;
  }

  .image-container {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 362px;
    border-radius: 0 4px 4px 0;
    overflow: hidden;
  }

  .content {
    max-width: 665px;
  }
}

@media (max-width: 1279px) {
  .image-container {
    display: none;
  }
}

@media (max-width: 500px) {
  .split-modal [class^='p-dialog-header'] .modal-title {
    max-width: 200px;
  }
}
</style>
